import { useState, useEffect } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'
import { api, apiErrorMessage } from '../api/client'
import type { ScrapingHealthResponse } from '../api/types'

const RANGE_OPTIONS = [7, 14, 30] as const
type Range = (typeof RANGE_OPTIONS)[number]

// ── Metric card ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div
      className="flex flex-col gap-1 px-5 py-4 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-glass)] backdrop-blur-[var(--glass-blur)] [-webkit-backdrop-filter:var(--glass-blur)] min-w-[120px]"
    >
      <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">{label}</span>
      <span className="text-[22px] font-[900] text-[var(--text-primary)] leading-none">{value}</span>
      {sub && <span className="text-[11px] text-[var(--text-muted)]">{sub}</span>}
    </div>
  )
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; fill: string }[]; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-[12px] shadow-lg">
      <p className="font-bold text-[var(--text-secondary)] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill }} className="mb-0.5">
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  )
}

// ── ScrapingHealthPanel ───────────────────────────────────────────────────────

export function ScrapingHealthPanel() {
  const [days, setDays] = useState<Range>(7)
  const [data, setData] = useState<ScrapingHealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    api
      .get<ScrapingHealthResponse>('/v1/analytics/scraping-health', { params: { days } })
      .then(setData)
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load')))
      .finally(() => setLoading(false))
  }, [days])

  const summary = data?.summary

  return (
    <div className="flex flex-col gap-6">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[18px] font-[900] text-[#dce9f7] m-0 mb-1 tracking-[-0.02em]">
            LinkedIn Scraping Health
          </h2>
          <p className="text-[12px] text-[var(--text-muted)] m-0">
            Per-day ok / blocked / error breakdown from run_steps output
          </p>
        </div>

        {/* Day range selector */}
        <div className="flex gap-1 bg-[var(--bg-glass)] border border-[var(--border)] rounded-[var(--radius-md)] p-1">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              className={[
                'px-3 py-1 rounded-[var(--radius-sm)] text-[12px] font-bold border-none cursor-pointer transition-colors duration-[120ms]',
                r === days
                  ? 'bg-[var(--accent-dim)] text-[var(--accent)]'
                  : 'bg-transparent text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-secondary)]',
              ].join(' ')}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary metric cards */}
      {summary && (
        <div className="flex flex-wrap gap-3">
          <MetricCard label="Total runs" value={summary.total} />
          <MetricCard
            label="Success rate"
            value={`${Math.round(summary.success_rate * 100)}%`}
            sub={`${summary.ok} ok`}
          />
          <MetricCard label="Blocked" value={summary.blocked} sub="auth wall" />
          <MetricCard label="Errors" value={summary.error} sub="network / parse" />
        </div>
      )}

      {/* Chart area */}
      <div
        className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-glass)] backdrop-blur-[var(--glass-blur)] [-webkit-backdrop-filter:var(--glass-blur)] p-5"
        style={{ minHeight: 280 }}
      >
        {loading && (
          <div className="flex items-center justify-center h-[240px] text-[13px] text-[var(--text-muted)]">
            Loading…
          </div>
        )}
        {!loading && error && (
          <div className="flex items-center justify-center h-[240px] text-[13px] text-red-400">
            {error}
          </div>
        )}
        {!loading && !error && data && (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.days} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                tickFormatter={(v: string) => v.slice(5)} // "MM-DD"
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Legend
                wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)', paddingTop: 8 }}
              />
              <Bar dataKey="ok"      name="ok"      stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="blocked" name="blocked" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
              <Bar dataKey="error"   name="error"   stackId="a" fill="#ef4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
